import get from 'lodash/get';
import map from 'lodash/map';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import minBy from 'lodash/minBy';
import meanBy from 'lodash/meanBy';
import round from 'lodash/round';

import { NormalizedGameData, NormalizedMatchData, NormalizedMatchPlayerData } from '../data_types/types';
import { HOLE_PREFIX_TO_DATA } from './holes';
import { normalizeHole } from './transformers/hole_data';
import { STATUS_ROUND_COMPLETE } from './const';

export type LayoutHole = {
    key: string;
    course: string;       // display name, or the raw prefix for unknown courses
    holeNum: number;
    par: number | null;   // null when the course or hole is missing from holes.ts
};

export type ScoreCell = { strokes: number | null; toPar: number | null };

export type ScoredCard = {
    scores: ScoreCell[];
    strokes: number;
    toPar: number;        // over the holes with known par only
    retired: boolean;
};

/**
 * Resolve every hole in the layout against holes.ts. Unknown courses (new or
 * custom event courses) resolve with par null instead of crashing.
 */
export const resolveLayout = (layout: string[]): LayoutHole[] =>
    map(layout, (key) => {
        const { holeShorthand, holeNum } = normalizeHole(key);
        const course = get(HOLE_PREFIX_TO_DATA, holeShorthand);
        const par = course?.par?.[holeNum - 1];
        if (isNil(par)) {
            console.warn(`scorecard: no par for hole "${key}" (prefix "${holeShorthand}"). Add it to holes.ts.`);
        }
        return { key, course: course?.name ?? holeShorthand, holeNum, par: isNil(par) ? null : par };
    });

/** Group consecutive holes on the same course, e.g. for a header row. */
export const courseSpans = (holes: LayoutHole[]) =>
    holes.reduce<{ course: string; span: number }[]>((spans, hole) => {
        const last = spans[spans.length - 1];
        if (last && last.course === hole.course) last.span += 1;
        else spans.push({ course: hole.course, span: 1 });
        return spans;
    }, []);

export const scorePlayer = (holes: LayoutHole[], player: NormalizedMatchPlayerData): ScoredCard => {
    const scores: ScoreCell[] = holes.map(hole => {
        const raw = player.scoreCard?.[hole.key];
        // 0 means the hole wasn't played (player retired)
        const strokes = isNil(raw) || raw === 0 ? null : Number(raw);
        return { strokes, toPar: strokes !== null && hole.par !== null ? strokes - hole.par : null };
    });
    return {
        scores,
        strokes: scores.reduce((sum, s) => sum + (s.strokes ?? 0), 0),
        toPar: scores.reduce((sum, s) => sum + (s.toPar ?? 0), 0),
        retired: player.status !== STATUS_ROUND_COMPLETE,
    };
};

export const formatToPar = (toPar: number, digits = 0) => {
    const value = Number(toPar.toFixed(digits));
    if (value === 0) return 'E';
    return `${value > 0 ? '+' : ''}${value}`;
};

/*****************************************************************************
 ** The signed-in player's own rounds
*****************************************************************************/

export type MyRound = {
    round: NormalizedGameData;
    match: NormalizedMatchData;
    holes: LayoutHole[];
    card: ScoredCard;
    knownPar: boolean;
};

/** Every round this season that has a scorecard, oldest first. */
export const getMyRounds = (myPID: string, historyData: NormalizedGameData[], matchData: NormalizedMatchData[]): MyRound[] => {
    const rounds: MyRound[] = [];
    [...historyData].sort((a, b) => a.matchNum - b.matchNum).forEach(round => {
        const match = find(matchData, ['timestamp', round.date]);
        const me = match && find(match.playerData, ['pID', myPID]);
        if (!match || !me) return;
        const holes = resolveLayout(match.layout ?? []);
        rounds.push({
            round,
            match,
            holes,
            card: scorePlayer(holes, me),
            knownPar: holes.every(h => h.par !== null),
        });
    });
    return rounds;
};

export type ScoringSummary = {
    rounds: number;
    averageToPar: number | null;
    best: MyRound | null;
};

/** Season to-par stats over completed rounds where every hole has a known par. */
export const getScoringSummary = (rounds: MyRound[]): ScoringSummary => {
    const counted = rounds.filter(r => r.knownPar && !r.card.retired);
    return {
        rounds: counted.length,
        averageToPar: counted.length ? round(meanBy(counted, r => r.card.toPar), 1) : null,
        best: minBy(counted, r => r.card.toPar) ?? null,
    };
};
